(function () {
  const sectionSelector = '[data-fill-product-section]';
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    'summary'
  ].join(',');

  const controllers = new WeakMap();

  function getFocusable(container) {
    return Array.from(container.querySelectorAll(focusableSelector)).filter((element) => {
      return element instanceof HTMLElement && (element.offsetParent !== null || element === document.activeElement);
    });
  }

  function parseVariants(section) {
    const script = section.querySelector('[data-fill-product-variants]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent || '[]');
    } catch (error) {
      console.warn('[fill-product] Variant data could not be parsed.', error);
      return [];
    }
  }

  function variantMatchesSelection(variant, selectedOptions) {
    return selectedOptions.every((value, index) => !value || variant.options[index] === value);
  }

  function hasPossibleVariant(variants, selectedOptions, position, value) {
    const testOptions = selectedOptions.slice();
    testOptions[position] = value;
    return variants.some((variant) => variantMatchesSelection(variant, testOptions));
  }

  class FillProduct {
    constructor(section) {
      this.section = section;
      this.variants = parseVariants(section);
      this.selectedOptions = [];
      this.currentVariant = null;
      this.currentMediaIndex = 0;
      this.lastFocus = null;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.collectInitialOptions();
      this.bind();
      this.setInitialMedia();
      this.updateFromSelection({ updateHistory: false });
      this.observeStickyBar();
      this.enableProductFormInput();
    }

    destroy() {
      this.abortController.abort();
      document.body.classList.remove('fill-product-modal-open');
      controllers.delete(this.section);
    }

    bind() {
      this.section.addEventListener('change', this.handleChange, { signal: this.signal });
      this.section.addEventListener('click', this.handleClick, { signal: this.signal });
      this.section.addEventListener('keydown', this.handleKeydown, { signal: this.signal });
      this.section.addEventListener('submit', this.handleSubmit, { signal: this.signal });
      this.section.addEventListener('touchstart', this.handleTouchStart, { passive: true, signal: this.signal });
      this.section.addEventListener('touchend', this.handleTouchEnd, { passive: true, signal: this.signal });
      document.addEventListener('shopify:cart:lines-update', this.handleCartLinesUpdate, { signal: this.signal });
    }

    collectInitialOptions() {
      const groups = this.optionGroups;
      this.selectedOptions = groups.map((group) => {
        const checked = group.querySelector('[data-fill-product-option]:checked');
        if (checked instanceof HTMLInputElement) return checked.value;

        const select = group.querySelector('select[data-fill-product-option]');
        if (select instanceof HTMLSelectElement) return select.value;

        return '';
      });
    }

    get optionGroups() {
      return Array.from(this.section.querySelectorAll('[data-fill-product-option-group]'));
    }

    get slides() {
      return Array.from(this.section.querySelectorAll('[data-fill-product-slide]'));
    }

    get activeSlide() {
      return this.slides[this.currentMediaIndex] || null;
    }

    enableProductFormInput() {
      const input = this.section.querySelector('[data-fill-product-variant-input]');
      if (input instanceof HTMLInputElement) {
        input.disabled = false;
      }
    }

    handleChange = (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;
      if (!target.matches('[data-fill-product-option]')) return;

      const position = Number.parseInt(target.dataset.optionPosition || '1', 10) - 1;
      this.selectedOptions[position] = target.value;
      this.updateFromSelection({ updateHistory: true });
    };

    handleClick = (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const thumb = target.closest('[data-fill-product-thumb]');
      if (thumb instanceof HTMLElement) {
        this.showMediaById(thumb.dataset.mediaId);
        return;
      }

      if (target.closest('[data-fill-product-prev]')) {
        this.showMediaByIndex(this.currentMediaIndex - 1);
        return;
      }

      if (target.closest('[data-fill-product-next]')) {
        this.showMediaByIndex(this.currentMediaIndex + 1);
        return;
      }

      const zoomTrigger = target.closest('[data-fill-product-zoom-trigger]');
      if (zoomTrigger instanceof HTMLButtonElement && !zoomTrigger.disabled) {
        const movedX = typeof this.pointerStartX === 'number' ? Math.abs((event.clientX || 0) - this.pointerStartX) : 0;
        if (movedX < 12) this.openZoom(zoomTrigger);
        return;
      }

      if (target.closest('[data-fill-product-zoom-close]')) {
        this.closeZoom();
        return;
      }

      if (target.closest('[data-fill-product-zoom-prev]')) {
        this.showZoomNeighbor(-1);
        return;
      }

      if (target.closest('[data-fill-product-zoom-next]')) {
        this.showZoomNeighbor(1);
        return;
      }

      if (target.closest('[data-fill-product-size-guide-trigger]')) {
        this.openModal('[data-fill-product-size-guide-modal]');
        return;
      }

      if (target.closest('[data-fill-product-size-guide-close]')) {
        this.closeModal('[data-fill-product-size-guide-modal]');
        return;
      }

      if (target.closest('[data-fill-product-sticky-add]')) {
        const addButton = this.section.querySelector('[data-fill-product-add-button]');
        if (addButton instanceof HTMLButtonElement && !addButton.disabled) addButton.click();
      }
    };

    handleKeydown = (event) => {
      const zoom = this.section.querySelector('[data-fill-product-zoom]');
      const sizeGuide = this.section.querySelector('[data-fill-product-size-guide-modal]');
      const activeDialog = [zoom, sizeGuide].find((dialog) => dialog instanceof HTMLElement && !dialog.hidden);

      if (event.key === 'Escape') {
        if (zoom instanceof HTMLElement && !zoom.hidden) this.closeZoom();
        if (sizeGuide instanceof HTMLElement && !sizeGuide.hidden) this.closeModal('[data-fill-product-size-guide-modal]');
        return;
      }

      if (event.key === 'ArrowLeft' && zoom instanceof HTMLElement && !zoom.hidden) {
        event.preventDefault();
        this.showZoomNeighbor(-1);
      }

      if (event.key === 'ArrowRight' && zoom instanceof HTMLElement && !zoom.hidden) {
        event.preventDefault();
        this.showZoomNeighbor(1);
      }

      if (event.key === 'Tab' && activeDialog instanceof HTMLElement) {
        this.trapFocus(event, activeDialog);
      }
    };

    handleSubmit = (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const addButton = this.section.querySelector('[data-fill-product-add-button]');
      if (!(addButton instanceof HTMLButtonElement) || !form.contains(addButton)) return;

      this.syncQuantityToForm();

      if (!this.currentVariant || !this.currentVariant.available) {
        event.preventDefault();
        this.updateAddButton();
        return;
      }

      addButton.dataset.fillProductState = 'loading';
      addButton.setAttribute('aria-busy', 'true');
    };

    handleCartLinesUpdate = (event) => {
      const sourceInsideSection = event.target instanceof Element && this.section.contains(event.target);
      if (!sourceInsideSection || event.action !== 'add') return;

      event.promise
        ?.then(({ detail }) => {
          this.clearLoadingState();
          if (detail?.didError) return;
          if (this.section.dataset.afterAddBehavior === 'cart') {
            window.location.href = this.section.dataset.cartUrl || '/cart';
          }
        })
        .catch(() => this.clearLoadingState());
    };

    handleTouchStart = (event) => {
      if (!event.target.closest('[data-fill-product-stage]')) return;
      const touch = event.touches[0];
      if (!touch) return;
      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.pointerStartX = touch.clientX;
    };

    handleTouchEnd = (event) => {
      if (!event.target.closest('[data-fill-product-stage]')) return;
      const touch = event.changedTouches[0];
      if (!touch) return;

      const deltaX = touch.clientX - this.touchStartX;
      const deltaY = touch.clientY - this.touchStartY;
      if (Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
        this.showMediaByIndex(this.currentMediaIndex + (deltaX < 0 ? 1 : -1));
      }
    };

    setInitialMedia() {
      const active = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      this.currentMediaIndex = active >= 0 ? active : 0;
      this.updateMediaState();
    }

    updateFromSelection({ updateHistory }) {
      this.currentVariant = this.resolveCurrentVariant();

      this.updateOptionStates();
      this.updateVariantInput();
      this.updatePrice();
      this.updateAvailability();
      this.updateQuantity();
      this.updateAddButton();
      this.updateVariantMedia();
      this.updateOptionLabels();
      this.updateStickyPrice();

      if (updateHistory && this.currentVariant && this.section.dataset.enableHistory === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', this.currentVariant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    resolveCurrentVariant() {
      if (!this.variants.length) return null;

      if (!this.selectedOptions.length) {
        const selectedVariantId = this.section.dataset.selectedVariantId;
        return this.variants.find((variant) => String(variant.id) === String(selectedVariantId)) ||
          this.variants.find((variant) => variant.selected) ||
          this.variants.find((variant) => variant.available) ||
          this.variants[0];
      }

      return this.variants.find((variant) => {
        return variant.options.every((optionValue, index) => optionValue === this.selectedOptions[index]);
      }) || null;
    }

    updateOptionStates() {
      this.optionGroups.forEach((group, groupIndex) => {
        const controls = group.querySelectorAll('[data-fill-product-option]');
        controls.forEach((control) => {
          const value = control.value;
          const possible = hasPossibleVariant(this.variants, this.selectedOptions, groupIndex, value);
          const variantForValue = this.variants.find((variant) => {
            const testOptions = this.selectedOptions.slice();
            testOptions[groupIndex] = value;
            return variantMatchesSelection(variant, testOptions);
          });
          const unavailable = possible && variantForValue && !variantForValue.available;

          if (control instanceof HTMLInputElement) {
            control.disabled = !possible;
            control.closest('.fill-product__option-choice')?.classList.toggle('is-unavailable', Boolean(unavailable));
            control.setAttribute('aria-disabled', unavailable || !possible ? 'true' : 'false');
          }

          if (control instanceof HTMLSelectElement) {
            Array.from(control.options).forEach((option) => {
              const optionPossible = hasPossibleVariant(this.variants, this.selectedOptions, groupIndex, option.value);
              option.disabled = !optionPossible;
            });
          }
        });
      });
    }

    updateVariantInput() {
      const input = this.section.querySelector('[data-fill-product-variant-input]');
      if (input instanceof HTMLInputElement) {
        input.value = this.currentVariant ? this.currentVariant.id : '';
      }
    }

    updatePrice() {
      const current = this.section.querySelector('[data-fill-product-current-price]');
      const compare = this.section.querySelector('[data-fill-product-compare-price]');
      const sale = this.section.querySelector('[data-fill-product-sale-label]');
      const unit = this.section.querySelector('[data-fill-product-unit-price]');
      if (!(current instanceof HTMLElement)) return;

      if (!this.currentVariant) {
        current.textContent = '';
        compare?.setAttribute('hidden', '');
        sale?.setAttribute('hidden', '');
        unit?.setAttribute('hidden', '');
        return;
      }

      current.textContent = this.currentVariant.priceText;
      const onSale = Number(this.currentVariant.compareAtPrice) > Number(this.currentVariant.price);

      if (compare instanceof HTMLElement) {
        compare.textContent = onSale ? this.currentVariant.compareAtPriceText || '' : '';
        compare.hidden = !onSale;
      }

      if (sale instanceof HTMLElement) sale.hidden = !onSale;

      if (unit instanceof HTMLElement) {
        unit.textContent = this.currentVariant.unitPriceText || '';
        unit.hidden = !this.currentVariant.unitPriceText;
      }
    }

    updateAvailability() {
      const availability = this.section.querySelector('[data-fill-product-availability]');
      if (!(availability instanceof HTMLElement)) return;

      const exact = this.section.dataset.showExactInventory === 'true';
      const threshold = Number.parseInt(this.section.dataset.lowStockThreshold || '6', 10);
      const variant = this.currentVariant;

      if (!variant) {
        availability.textContent = 'Sold out';
        availability.dataset.status = 'sold-out';
        return;
      }

      if (!variant.available) {
        availability.textContent = 'Sold out';
        availability.dataset.status = 'sold-out';
        return;
      }

      if (variant.inventoryManagement === 'shopify' && variant.inventoryPolicy !== 'continue') {
        const quantity = Number(variant.inventoryQuantity);
        if (quantity > 0 && quantity <= threshold) {
          availability.textContent = exact ? `${quantity} in stock` : 'Low stock';
          availability.dataset.status = 'low';
          return;
        }
      }

      availability.textContent = 'In stock';
      availability.dataset.status = 'in-stock';
    }

    updateQuantity() {
      const variant = this.currentVariant;
      const input = this.section.querySelector('quantity-selector-component input[ref="quantityInput"]');
      const hiddenQuantity = this.section.querySelector('[data-fill-product-hidden-quantity]');
      const min = variant?.quantityRule?.min || 1;
      const increment = variant?.quantityRule?.increment || 1;
      const max = variant?.quantityRule?.max || '';

      if (input instanceof HTMLInputElement) {
        input.min = String(min);
        input.step = String(increment);
        if (max) input.max = String(max);
        else input.removeAttribute('max');
        if (!input.value || Number(input.value) < min) input.value = String(min);
        input.disabled = !variant || !variant.available;
        const quantitySelector = input.closest('quantity-selector-component');
        if (quantitySelector && typeof quantitySelector.updateConstraints === 'function') {
          quantitySelector.updateConstraints(String(min), max ? String(max) : null, String(increment));
        }
      }

      if (hiddenQuantity instanceof HTMLInputElement) {
        hiddenQuantity.value = input instanceof HTMLInputElement ? input.value || String(min) : String(min);
      }
    }

    updateAddButton() {
      const button = this.section.querySelector('[data-fill-product-add-button]');
      const text = this.section.querySelector('[data-fill-product-add-text]');
      const dynamicCheckout = this.section.querySelector('[data-fill-product-dynamic-checkout]');
      const stickyButton = this.section.querySelector('[data-fill-product-sticky-add]');
      const stickyText = this.section.querySelector('[data-fill-product-sticky-text]');
      if (!(button instanceof HTMLButtonElement)) return;

      let label = 'Sold Out';
      let disabled = true;

      if (this.currentVariant) {
        if (this.currentVariant.available) {
          label = 'Add to Bag';
          disabled = false;
        } else {
          label = 'Sold Out';
        }
      }

      button.disabled = disabled;
      if (text instanceof HTMLElement) text.textContent = label;
      if (dynamicCheckout instanceof HTMLElement) dynamicCheckout.hidden = disabled;

      if (stickyButton instanceof HTMLButtonElement) {
        stickyButton.disabled = disabled;
      }
      if (stickyText instanceof HTMLElement) stickyText.textContent = label;
    }

    syncQuantityToForm() {
      const visibleQuantity = this.section.querySelector('quantity-selector-component input[ref="quantityInput"]');
      const hiddenQuantity = this.section.querySelector('[data-fill-product-hidden-quantity]');
      if (visibleQuantity instanceof HTMLInputElement && hiddenQuantity instanceof HTMLInputElement) {
        hiddenQuantity.value = visibleQuantity.value || visibleQuantity.min || '1';
      }
    }

    updateVariantMedia() {
      const mediaId = this.currentVariant?.featuredMediaId;
      if (mediaId) this.showMediaById(String(mediaId), { silent: true });

      const addContainer = this.section.querySelector('add-to-cart-component[ref="addToCartButtonContainer"]');
      if (addContainer instanceof HTMLElement && this.currentVariant?.featuredMediaImage) {
        addContainer.dataset.productVariantMedia = this.currentVariant.featuredMediaImage;
      }
    }

    updateOptionLabels() {
      this.optionGroups.forEach((group, index) => {
        const label = group.querySelector('[data-fill-product-option-current]');
        if (label instanceof HTMLElement) label.textContent = this.selectedOptions[index] || '';
      });
    }

    updateStickyPrice() {
      const stickyPrice = this.section.querySelector('[data-fill-product-sticky-price]');
      if (stickyPrice instanceof HTMLElement && this.currentVariant) {
        stickyPrice.textContent = this.currentVariant.priceText;
      }
    }

    clearLoadingState() {
      const addButton = this.section.querySelector('[data-fill-product-add-button]');
      if (addButton instanceof HTMLButtonElement) {
        delete addButton.dataset.fillProductState;
        addButton.removeAttribute('aria-busy');
      }
    }

    showMediaById(mediaId, options = {}) {
      const index = this.slides.findIndex((slide) => slide instanceof HTMLElement && slide.dataset.mediaId === String(mediaId));
      if (index >= 0) this.showMediaByIndex(index, options);
    }

    showMediaByIndex(index, options = {}) {
      const slides = this.slides;
      if (!slides.length) return;
      const nextIndex = (index + slides.length) % slides.length;
      this.currentMediaIndex = nextIndex;
      this.updateMediaState();
      if (!options.silent) this.activeSlide?.querySelector('[data-fill-product-zoom-trigger]')?.focus?.();
    }

    updateMediaState() {
      this.slides.forEach((slide, index) => {
        const active = index === this.currentMediaIndex;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
      });

      const activeSlide = this.activeSlide;
      const activeMediaId = activeSlide instanceof HTMLElement ? activeSlide.dataset.mediaId : '';
      this.section.querySelectorAll('[data-fill-product-thumb]').forEach((thumb) => {
        const active = thumb instanceof HTMLElement && thumb.dataset.mediaId === activeMediaId;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });

      const counter = this.section.querySelector('[data-fill-product-counter]');
      if (counter instanceof HTMLElement) {
        counter.textContent = `${this.currentMediaIndex + 1} / ${this.slides.length}`;
      }
    }

    openZoom(trigger) {
      const zoom = this.section.querySelector('[data-fill-product-zoom]');
      const image = this.section.querySelector('[data-fill-product-zoom-image]');
      const src = trigger.dataset.zoomSrc;
      if (!(zoom instanceof HTMLElement) || !(image instanceof HTMLImageElement) || !src) return;

      this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
      image.src = src;
      image.alt = trigger.dataset.zoomAlt || '';
      zoom.hidden = false;
      document.body.classList.add('fill-product-modal-open');
      const close = zoom.querySelector('[data-fill-product-zoom-close]');
      if (close instanceof HTMLElement) close.focus();
    }

    closeZoom() {
      const zoom = this.section.querySelector('[data-fill-product-zoom]');
      const image = this.section.querySelector('[data-fill-product-zoom-image]');
      if (!(zoom instanceof HTMLElement)) return;
      zoom.hidden = true;
      if (image instanceof HTMLImageElement) image.removeAttribute('src');
      document.body.classList.remove('fill-product-modal-open');
      this.lastFocus?.focus?.();
    }

    showZoomNeighbor(direction) {
      const imageSlides = this.slides.filter((slide) => slide instanceof HTMLElement && slide.dataset.mediaType === 'image');
      if (!imageSlides.length) return;
      const active = this.activeSlide;
      const currentImageIndex = imageSlides.indexOf(active);
      const nextImage = imageSlides[(currentImageIndex + direction + imageSlides.length) % imageSlides.length];
      if (!(nextImage instanceof HTMLElement)) return;
      this.showMediaById(nextImage.dataset.mediaId, { silent: true });
      const trigger = nextImage.querySelector('[data-fill-product-zoom-trigger]');
      if (trigger instanceof HTMLButtonElement) this.openZoom(trigger);
    }

    openModal(selector) {
      const modal = this.section.querySelector(selector);
      if (!(modal instanceof HTMLElement)) return;

      this.lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      modal.hidden = false;
      document.body.classList.add('fill-product-modal-open');
      const focusable = getFocusable(modal);
      (focusable[0] || modal).focus();
    }

    closeModal(selector) {
      const modal = this.section.querySelector(selector);
      if (!(modal instanceof HTMLElement)) return;

      modal.hidden = true;
      document.body.classList.remove('fill-product-modal-open');
      this.lastFocus?.focus?.();
    }

    trapFocus(event, container) {
      const focusable = getFocusable(container);
      if (!focusable.length) {
        event.preventDefault();
        container.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    observeStickyBar() {
      const bar = this.section.querySelector('[data-fill-product-sticky-bar]');
      const button = this.section.querySelector('[data-fill-product-add-button]');
      if (!(bar instanceof HTMLElement) || !(button instanceof HTMLElement)) return;
      if (window.Shopify?.designMode) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (window.matchMedia('(min-width: 750px)').matches) {
            bar.hidden = true;
            return;
          }
          bar.hidden = entry.isIntersecting || !this.currentVariant || !this.currentVariant.available;
        },
        { threshold: 0.08 }
      );

      observer.observe(button);
      this.signal.addEventListener('abort', () => observer.disconnect(), { once: true });
    }
  }

  function init(root = document) {
    root.querySelectorAll(sectionSelector).forEach((section) => {
      if (!(section instanceof HTMLElement) || controllers.has(section)) return;
      controllers.set(section, new FillProduct(section));
    });
  }

  function unload(root = document) {
    root.querySelectorAll(sectionSelector).forEach((section) => {
      controllers.get(section)?.destroy();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
})();
